import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    Plus,
    Trash2,
    Edit3,
    Briefcase,
    Users,
    Mail,
    ExternalLink,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
    open: "border-emerald-400/40 text-emerald-400",
    closed: "border-white/15 text-bone-500",
    draft: "border-amber-400/40 text-amber-400",
};

const APP_STATUS_BADGE = {
    new: "border-ember/40 text-ember",
    reviewing: "border-amber-400/40 text-amber-400",
    shortlisted: "border-sky-400/40 text-sky-400",
    interview: "border-violet-400/40 text-violet-400",
    hired: "border-emerald-400/40 text-emerald-400",
    rejected: "border-white/15 text-bone-500",
};

export default function AdminCareers() {
    const { user, token, loading } = useAuth();
    const [tab, setTab] = useState("jobs");
    const [jobs, setJobs] = useState([]);
    const [apps, setApps] = useState([]);
    const [busy, setBusy] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} | job

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () => {
        setBusy(true);
        return Promise.all([
            axios.get(`${API}/admin/careers`, { headers }),
            axios.get(`${API}/admin/applications`, { headers }),
        ])
            .then(([j, a]) => {
                setJobs(j.data || []);
                setApps(a.data || []);
            })
            .catch(() => toast.error("Could not load careers data"))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!user) return <Navigate to="/admin/login" replace />;

    const deleteJob = async (id) => {
        if (!window.confirm("Delete this posting? Existing applications stay.")) return;
        try {
            await axios.delete(`${API}/admin/careers/${id}`, { headers });
            toast.success("Posting deleted");
            refresh();
        } catch {
            toast.error("Could not delete");
        }
    };

    const updateAppStatus = async (id, status) => {
        try {
            await axios.patch(
                `${API}/admin/applications/${id}`,
                { status },
                { headers },
            );
            toast.success("Status updated");
            refresh();
        } catch {
            toast.error("Could not update");
        }
    };

    const newApps = apps.filter((a) => a.status === "new").length;

    return (
        <AdminShell>
            <div className="max-w-6xl">
                <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-4 h-4 text-ember" />
                    <p className="text-xs uppercase tracking-[0.25em] text-ember">
                        Briefcase · Hiring
                    </p>
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[0.95] mb-10">
                    Careers.
                </h1>

                <div className="flex items-center justify-between gap-3 mb-8 border-b border-white/10 pb-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        {[
                            {
                                v: "jobs",
                                label: `Postings · ${jobs.length}`,
                                icon: Briefcase,
                            },
                            {
                                v: "apps",
                                label: `Applications · ${apps.length}${newApps ? ` (${newApps} new)` : ""}`,
                                icon: Users,
                            },
                        ].map((t) => {
                            const I = t.icon;
                            return (
                                <button
                                    key={t.v}
                                    onClick={() => setTab(t.v)}
                                    data-testid={`careers-tab-${t.v}`}
                                    className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border inline-flex items-center gap-2 transition-colors ${
                                        tab === t.v
                                            ? "border-ember text-ember"
                                            : "border-white/15 text-bone-300 hover:border-white/30"
                                    }`}
                                >
                                    <I className="w-3.5 h-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                    {tab === "jobs" && (
                        <button
                            onClick={() => setEditing({})}
                            data-testid="new-job-btn"
                            className="btn-ember inline-flex items-center gap-2 h-10 px-5 text-xs uppercase tracking-[0.2em]"
                        >
                            <Plus className="w-4 h-4" />
                            New posting
                        </button>
                    )}
                </div>

                {busy ? (
                    <div className="text-bone-500 text-sm flex items-center gap-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading…
                    </div>
                ) : tab === "jobs" ? (
                    <JobsTable
                        jobs={jobs}
                        onEdit={setEditing}
                        onDelete={deleteJob}
                    />
                ) : (
                    <ApplicationsTable
                        apps={apps}
                        onUpdateStatus={updateAppStatus}
                    />
                )}
            </div>

            {editing && (
                <JobEditModal
                    job={editing}
                    headers={headers}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        refresh();
                    }}
                />
            )}
        </AdminShell>
    );
}

function JobsTable({ jobs, onEdit, onDelete }) {
    if (jobs.length === 0) {
        return (
            <p className="text-bone-500 text-sm border border-dashed border-white/10 p-12 text-center">
                No postings yet. Create your first one to start receiving
                applications.
            </p>
        );
    }
    return (
        <ul data-testid="admin-jobs-list" className="space-y-3">
            {jobs.map((j) => (
                <li
                    key={j.id}
                    data-testid={`admin-job-${j.id}`}
                    className="border border-white/10 bg-ink-800/40 p-5 hover:border-ember/40 transition-colors"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span
                                    className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${STATUS_BADGE[j.status] || ""}`}
                                >
                                    {j.status}
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                    {j.employment_type} · {j.location}
                                </span>
                                {j.application_count > 0 && (
                                    <span className="text-[10px] uppercase tracking-[0.2em] border border-ember/40 text-ember px-2 py-1">
                                        {j.application_count} application
                                        {j.application_count > 1 ? "s" : ""}
                                    </span>
                                )}
                            </div>
                            <h3 className="font-display text-bone text-xl tracking-tight">
                                {j.title}
                            </h3>
                            {j.department && (
                                <p className="text-xs text-bone-500 mt-1">
                                    {j.department}
                                </p>
                            )}
                            <p className="text-bone-300 text-sm mt-2 line-clamp-2">
                                {j.summary}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={() => onEdit(j)}
                                data-testid={`edit-job-${j.id}`}
                                className="btn-outline-bone inline-flex items-center gap-1 h-9 px-3 text-xs uppercase tracking-[0.18em]"
                            >
                                <Edit3 className="w-3 h-3" />
                                Edit
                            </button>
                            <button
                                onClick={() => onDelete(j.id)}
                                data-testid={`delete-job-${j.id}`}
                                className="text-bone-500 hover:text-red-400 h-9 px-3 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1"
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </button>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

function ApplicationsTable({ apps, onUpdateStatus }) {
    if (apps.length === 0) {
        return (
            <p className="text-bone-500 text-sm border border-dashed border-white/10 p-12 text-center">
                No applications yet. Share your careers page link to start
                receiving submissions.
            </p>
        );
    }
    const statuses = [
        "new",
        "reviewing",
        "shortlisted",
        "interview",
        "hired",
        "rejected",
    ];
    return (
        <ul data-testid="admin-apps-list" className="space-y-3">
            {apps.map((a) => (
                <li
                    key={a.id}
                    data-testid={`admin-app-${a.id}`}
                    className="border border-white/10 bg-ink-800/40 p-5"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                        <div className="min-w-0 flex-1">
                            <p className="font-display text-bone text-lg tracking-tight">
                                {a.name}
                            </p>
                            <a
                                href={`mailto:${a.email}`}
                                className="text-xs text-bone-500 hover:text-ember inline-flex items-center gap-1 transition-colors"
                            >
                                <Mail className="w-3 h-3" />
                                {a.email}
                                {a.phone && <span> · {a.phone}</span>}
                            </a>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-ember mt-2">
                                {a.job_title}{" "}
                                {a.role_interest && `· ${a.role_interest}`}
                            </p>
                        </div>
                        <select
                            value={a.status}
                            onChange={(e) =>
                                onUpdateStatus(a.id, e.target.value)
                            }
                            data-testid={`status-${a.id}`}
                            className={`bg-ink border px-3 h-9 text-xs uppercase tracking-[0.18em] outline-none ${APP_STATUS_BADGE[a.status] || "border-white/15 text-bone-300"}`}
                        >
                            {statuses.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-bone-500 mb-3">
                        {a.location && <span>📍 {a.location}</span>}
                        {a.portfolio_url && (
                            <a
                                href={a.portfolio_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-ember hover:text-bone inline-flex items-center gap-1 transition-colors"
                            >
                                Portfolio <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                        {a.linkedin_url && (
                            <a
                                href={a.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-ember hover:text-bone inline-flex items-center gap-1 transition-colors"
                            >
                                LinkedIn <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                        {a.resume_url && (
                            <a
                                href={a.resume_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-ember hover:text-bone inline-flex items-center gap-1 transition-colors"
                            >
                                Resume <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                        <span className="ml-auto">
                            {new Date(a.created_at).toLocaleDateString()}
                        </span>
                    </div>

                    {a.cover_letter && (
                        <details className="text-bone-300 text-sm">
                            <summary className="cursor-pointer text-bone-500 text-xs uppercase tracking-[0.2em] hover:text-ember">
                                Cover letter
                            </summary>
                            <p className="mt-3 whitespace-pre-wrap leading-relaxed">
                                {a.cover_letter}
                            </p>
                        </details>
                    )}
                </li>
            ))}
        </ul>
    );
}

function JobEditModal({ job, headers, onClose, onSaved }) {
    const isNew = !job?.id;
    const [form, setForm] = useState(() => ({
        title: job?.title || "",
        department: job?.department || "",
        employment_type: job?.employment_type || "full-time",
        location: job?.location || "Remote",
        summary: job?.summary || "",
        description: job?.description || "",
        requirements: (job?.requirements || []).join("\n"),
        perks: (job?.perks || []).join("\n"),
        salary_range: job?.salary_range || "",
        apply_email: job?.apply_email || "",
        status: job?.status || "open",
    }));
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.summary.trim()) {
            toast.error("Title and summary are required");
            return;
        }
        setBusy(true);
        const payload = {
            ...form,
            requirements: form.requirements
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            perks: form.perks
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            apply_email: form.apply_email || null,
            description: form.description || null,
            salary_range: form.salary_range || null,
            department: form.department || null,
        };
        try {
            if (isNew) {
                await axios.post(`${API}/admin/careers`, payload, { headers });
                toast.success("Posting created");
            } else {
                await axios.patch(
                    `${API}/admin/careers/${job.id}`,
                    payload,
                    { headers },
                );
                toast.success("Posting updated");
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-ink border border-white/15 max-w-2xl w-full p-7 lg:p-10 my-8">
                <div className="flex items-start justify-between mb-6 gap-4">
                    <h3 className="font-display text-bone text-3xl tracking-tightest">
                        {isNew ? "New posting" : "Edit posting"}
                    </h3>
                    <button
                        onClick={onClose}
                        data-testid="job-modal-close"
                        className="text-bone-500 hover:text-bone"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form
                    onSubmit={onSubmit}
                    data-testid="job-form"
                    className="space-y-5"
                >
                    <Field label="Title *" value={form.title} onChange={set("title")} testid="job-title" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Department" value={form.department} onChange={set("department")} testid="job-dept" />
                        <Field label="Location" value={form.location} onChange={set("location")} testid="job-location" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Employment type"
                            value={form.employment_type}
                            onChange={set("employment_type")}
                            testid="job-type"
                            options={[
                                "full-time",
                                "part-time",
                                "contract",
                                "freelance",
                                "internship",
                            ]}
                        />
                        <Select
                            label="Status"
                            value={form.status}
                            onChange={set("status")}
                            testid="job-status"
                            options={["open", "draft", "closed"]}
                        />
                    </div>
                    <Field label="Salary range" value={form.salary_range} onChange={set("salary_range")} testid="job-salary" placeholder="$70k–$100k or DOE" />
                    <Field label="Apply-to email override" type="email" value={form.apply_email} onChange={set("apply_email")} testid="job-apply-email" placeholder="careers@aximo.studio (optional)" />
                    <Textarea label="Summary * (1-2 lines)" rows={2} value={form.summary} onChange={set("summary")} testid="job-summary" />
                    <Textarea label="Full description" rows={5} value={form.description} onChange={set("description")} testid="job-description" />
                    <Textarea label="Requirements (one per line)" rows={4} value={form.requirements} onChange={set("requirements")} testid="job-reqs" />
                    <Textarea label="Perks (one per line)" rows={3} value={form.perks} onChange={set("perks")} testid="job-perks" />

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="job-submit"
                        className="btn-ember w-full inline-flex items-center justify-center gap-2 h-12 px-5 text-sm tracking-wide disabled:opacity-60"
                    >
                        {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>{isNew ? "Create posting" : "Save changes"}</>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-2.5 text-bone placeholder:text-bone-500 text-sm transition-colors"
            />
        </label>
    );
}

function Textarea({ label, value, onChange, testid, rows = 3 }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <textarea
                value={value}
                onChange={onChange}
                rows={rows}
                data-testid={testid}
                className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
            />
        </label>
    );
}

function Select({ label, value, onChange, testid, options }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <select
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full bg-ink border border-white/15 focus:border-ember outline-none px-3 h-10 text-bone text-xs uppercase tracking-[0.18em]"
            >
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}
